<template>
  <div class="map-container d-flex flex-column flex-grow-1 position-relative" style="min-height: 600px">
    <div id="map" class="w-100 d-flex flex-grow-1"></div>
  </div>
</template>
<script>
import { compressToUTF16, decompressFromUTF16 } from 'lz-string';

let mapInstance = null;

export default {
  props: [
    'mapKey',
    'mapId',
    'canSeeChargePoints',
    'canSeeHubject',
    'userEmpId',
    'userCpoId',
    'coordinates',
    'center',
    'zoom',
    'reset',
    'showClusters',
  ],
  data: function () {
    return {
      markers: [],
      hubjectMarkers: {},
      newMarkers: null,
      map: null,
      infoWindow: null,
      zoomLevel: 8,
      geoHubjectMarkers: {},
      groupMarkers: [],
      out: true,
      currentOverlay: null,
      clusters: [],
      currentZoomLevel: null,
      clusterOnScreen: false,
      initial13Load: null,
    };
  },
  watch: {
    coordinates: {
      handler: async function () {
        if (!this.coordinates) {
          return;
        }

        const [lat, lng] = this.coordinates.split('_');
        this.map.setCenter({ lat: +lat, lng: +lng });
        this.map.setZoom(15);

        await this.getHubjectData();

        // hubject markers lat and lng are rounded to 5 decimal places when getting the data from the database
        // so here we mimic the same rounding to get the correct key
        const key = this.mysqlRound(+lat) + '_' + this.mysqlRound(+lng);
        if (this.hubjectMarkers[key] && this.hubjectMarkers[key].marker) {
          google.maps.event.trigger(this.hubjectMarkers[key].marker, 'click');
        }
      },
    },
    reset: {
      handler: function () {
        this.resetMap();
      },
    },
    showClusters: {
      handler: async function () {
        if (this.showClusters) {
          google.maps.event.trigger(this.map, 'idle');
        } else {
          this.cleanClusters();
        }
      },
    },
  },
  methods: {
    resetMap: function () {
      const center = this.center.split('_');
      this.map.setCenter({ lat: +center[0], lng: +center[1] });
      this.map.setZoom(this.zoom);
    },
    getData: function () {
      axios
        .get('get-map')
        .then((response) => {
          this.newMarkers = response.data.map;
          this.cleanMarkers();
          this.setMarkers();
        })
        .catch((error) => {
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.log(error.response);
          } else {
            // Something happened in setting up the request that triggered an Error
            console.log('Error', error.message);
          }
        });
    },
    getHubjectData: async function (status = false, ids = []) {
      try {
        const response = await axios.get('admin/hubject-emp/map-data', {
          params: {
            status: status,
            sw_latitude: status ? null : mapInstance.getBounds().getSouthWest().lat(),
            sw_longitude: status ? null : mapInstance.getBounds().getSouthWest().lng(),
            ne_latitude: status ? null : mapInstance.getBounds().getNorthEast().lat(),
            ne_longitude: status ? null : mapInstance.getBounds().getNorthEast().lng(),
            ids: ids,
          },
        });

        if (status) {
          this.updateHubjectMarkers(response.data.data);
        } else {
          this.prepareHubjectMarkers(response.data.data);
        }
      } catch (error) {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.log(error.response);
        } else {
          // Something happened in setting up the request that triggered an Error
        }
      }
    },
    getHubjectDescription: async function (id) {
      return new Promise((resolve) => {
        axios
          .get('admin/hubject-emp/map-info', {
            params: {
              id: id,
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            if (error.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              console.log(error.response);
            } else {
              // Something happened in setting up the request that triggered an Error
            }

            resolve([]);
          });
      });
    },
    cleanMarkers: function () {
      for (const marker of Object.values(this.markers)) {
        marker.marker.setMap(null);
      }

      this.markers = {};
    },
    cleanHubjectMarkers: function () {
      // console.log('Cleaning hubject markers');
      for (const marker of Object.values(this.hubjectMarkers)) {
        if (marker.marker) {
          marker.marker.setMap(null);
        }
      }

      this.hubjectMarkers = {};
    },
    assignPin: function (status, availability, emergencyStopStatus) {
      const sPublic = '/assets/images/cp-';
      const sPrivate = '/assets/images/cp-private-';
      const offline = sPublic + 'offline.svg';

      const available = 'available.svg';
      const occupied = 'occupied.svg';
      const faulted = 'faulted.svg';
      const multiple = 'multiple.svg';

      const availableOccupied = 'available-occupied.svg';
      const availableFaulted = 'available-faulted.svg';
      const availableOffline = 'available-offline.svg';
      const occupiedFaulted = 'occupied-faulted.svg';
      const occupiedOffline = 'occupied-offline.svg';
      const offlineFaulted = 'faulted-offline.svg';

      const prefix = availability.indexOf('private') !== -1 ? sPrivate : sPublic;
      if (emergencyStopStatus === true) {
        return sPublic + 'emergency-stop.svg';
      }

      if (status.length === 1) {
        switch (status[0]) {
          case 'offline':
            return offline;
          case 'available':
            return prefix + available;
          case 'reserved':
          case 'occupied':
            return prefix + occupied;
          default:
            return prefix + faulted;
        }
      }

      if (status.length === 2) {
        const hasAvailable = status.indexOf('available') !== -1;
        const hasOffline = status.indexOf('offline') !== -1;
        const hasOccupied = status.indexOf('reserved') !== -1 || status.indexOf('occupied') !== -1;

        if (hasAvailable && hasOccupied) {
          return prefix + availableOccupied;
        } else if (hasAvailable && hasOffline) {
          return prefix + availableOffline;
        } else if (hasAvailable) {
          return prefix + availableFaulted;
        } else if (hasOccupied && hasOffline) {
          return prefix + occupiedOffline;
        } else if (hasOccupied) {
          return prefix + occupiedFaulted;
        } else {
          return prefix + offlineFaulted;
        }
      }

      return prefix + multiple;
    },
    assignHubjectPin: function (markerData) {
      if (markerData === 1) {
        return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzEiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAzMSA0MCI+CiAgPGRlZnM+CiAgICA8Y2xpcFBhdGggaWQ9ImNsaXAtcGF0aCI+CiAgICAgIDxyZWN0IGlkPSJSZWN0YW5nbGVfNTYwOSIgZGF0YS1uYW1lPSJSZWN0YW5nbGUgNTYwOSIgd2lkdGg9IjMxIiBoZWlnaHQ9IjQwIiBmaWxsPSIjZmZmIi8+CiAgICA8L2NsaXBQYXRoPgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfMTA0NDciIGRhdGEtbmFtZT0iR3JvdXAgMTA0NDciIGNsaXAtcGF0aD0idXJsKCNjbGlwLXBhdGgpIj4KICAgIDxnIGlkPSJHcm91cF8xMDQ0NiIgZGF0YS1uYW1lPSJHcm91cCAxMDQ0NiI+CiAgICAgIDxwYXRoIGlkPSJQYXRoXzQ5MTkiIGRhdGEtbmFtZT0iUGF0aCA0OTE5IiBkPSJNMjYuMyw0LjNBMTUuMjczLDE1LjI3MywwLDAsMCwxNS41LDAsMTUuMzc0LDE1LjM3NCwwLDAsMCwwLDE1LjljMCwzLjIsMS4zLDYuNywzLjgsMTAuNnM2LjUsOC4xLDExLjcsMTIuNmM1LjItNC41LDkuMS04LjcsMTEuNi0xMi42LDIuNi0zLjksMy45LTcuNCwzLjktMTAuNkExNS4yMTQsMTUuMjE0LDAsMCwwLDI2LjMsNC4zWiIgZmlsbD0iIzAwMzNGRiIvPgogICAgICA8cGF0aCBpZD0iUGF0aF80OTIwIiBkYXRhLW5hbWU9IlBhdGggNDkyMCIgZD0iTTE1LjUsMzUuNUE1NS41NjUsNTUuNTY1LDAsMCwxLDYsMjQuN2MtMi4yLTMuNC0zLjMtNi4zLTMuMy04LjhBMTIuNzMyLDEyLjczMiwwLDAsMSw2LjQsNi40YTEyLjI5MywxMi4yOTMsMCwwLDEsOS4xLTMuNywxMi4xMzksMTIuMTM5LDAsMCwxLDkuMSwzLjcsMTIuNzMyLDEyLjczMiwwLDAsMSwzLjcsOS41YzAsMi41LTEuMSw1LjQtMy4yLDguOEE1Ni40NTcsNTYuNDU3LDAsMCwxLDE1LjUsMzUuNVoiIGZpbGw9IiMzYzAiLz4KICAgICAgPHBhdGggaWQ9IlBhdGhfNDkyMSIgZGF0YS1uYW1lPSJQYXRoIDQ5MjEiIGQ9Ik0xNS41LDM1LjVWMi43YTEyLjEzOSwxMi4xMzksMCwwLDEsOS4xLDMuNywxMi43MzIsMTIuNzMyLDAsMCwxLDMuNyw5LjVjMCwyLjUtMS4xLDUuNC0zLjIsOC44QTU2LjQ1Nyw1Ni40NTcsMCwwLDEsMTUuNSwzNS41WiIgZmlsbD0ib3JhbmdlIi8+CiAgICAgIDxwYXRoIGlkPSJQYXRoXzQ5MjIiIGRhdGEtbmFtZT0iUGF0aCA0OTIyIiBkPSJNMTUuNDksMTYuMUgyMS41TDE1LjQ5LDI4LjMzN3YtOC4yTDkuNCwyMC4yLDE1LjQ5LDcuOVoiIGZpbGw9IiNmZmYiLz4KICAgIDwvZz4KICA8L2c+Cjwvc3ZnPgo=';
      } else if (markerData === 2) {
        return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzEiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAzMSA0MCI+CiAgPGRlZnM+CiAgICA8Y2xpcFBhdGggaWQ9ImNsaXAtcGF0aCI+CiAgICAgIDxyZWN0IGlkPSJSZWN0YW5nbGVfNTYwOSIgZGF0YS1uYW1lPSJSZWN0YW5nbGUgNTYwOSIgd2lkdGg9IjMxIiBoZWlnaHQ9IjQwIiBmaWxsPSIjZmZmIi8+CiAgICA8L2NsaXBQYXRoPgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfMTA0NDciIGRhdGEtbmFtZT0iR3JvdXAgMTA0NDciIGNsaXAtcGF0aD0idXJsKCNjbGlwLXBhdGgpIj4KICAgIDxnIGlkPSJHcm91cF8xMDQ0NiIgZGF0YS1uYW1lPSJHcm91cCAxMDQ0NiI+CiAgICAgIDxwYXRoIGlkPSJQYXRoXzQ5MTkiIGRhdGEtbmFtZT0iUGF0aCA0OTE5IiBkPSJNMjYuMyw0LjNBMTUuMjczLDE1LjI3MywwLDAsMCwxNS41LDAsMTUuMzc0LDE1LjM3NCwwLDAsMCwwLDE1LjljMCwzLjIsMS4zLDYuNywzLjgsMTAuNnM2LjUsOC4xLDExLjcsMTIuNmM1LjItNC41LDkuMS04LjcsMTEuNi0xMi42LDIuNi0zLjksMy45LTcuNCwzLjktMTAuNkExNS4yMTQsMTUuMjE0LDAsMCwwLDI2LjMsNC4zWiIgZmlsbD0iIzAwMzNGRiIvPgogICAgICA8cGF0aCBpZD0iUGF0aF80OTIwIiBkYXRhLW5hbWU9IlBhdGggNDkyMCIgZD0iTTE1LjUsMzUuNUE1NS41NjUsNTUuNTY1LDAsMCwxLDYsMjQuN2MtMi4yLTMuNC0zLjMtNi4zLTMuMy04LjhBMTIuNzMyLDEyLjczMiwwLDAsMSw2LjQsNi40YTEyLjI5MywxMi4yOTMsMCwwLDEsOS4xLTMuNywxMi4xMzksMTIuMTM5LDAsMCwxLDkuMSwzLjcsMTIuNzMyLDEyLjczMiwwLDAsMSwzLjcsOS41YzAsMi41LTEuMSw1LjQtMy4yLDguOEE1Ni40NTcsNTYuNDU3LDAsMCwxLDE1LjUsMzUuNVoiIGZpbGw9IiMzYzAiLz4KICAgICAgPHBhdGggaWQ9IlBhdGhfNDkyMSIgZGF0YS1uYW1lPSJQYXRoIDQ5MjEiIGQ9Ik0xNS41LDM1LjVWMi43YTEyLjEzOSwxMi4xMzksMCwwLDEsOS4xLDMuNywxMi43MzIsMTIuNzMyLDAsMCwxLDMuNyw5LjVjMCwyLjUtMS4xLDUuNC0zLjIsOC44QTU2LjQ1Nyw1Ni40NTcsMCwwLDEsMTUuNSwzNS41WiIgZmlsbD0iI0ZGMzMwMCIvPgogICAgICA8cGF0aCBpZD0iUGF0aF80OTIyIiBkYXRhLW5hbWU9IlBhdGggNDkyMiIgZD0iTTE1LjQ5LDE2LjFIMjEuNUwxNS40OSwyOC4zMzd2LTguMkw5LjQsMjAuMiwxNS40OSw3LjlaIiBmaWxsPSIjZmZmIi8+CiAgICA8L2c+CiAgPC9nPgo8L3N2Zz4K';
      } else if (markerData === 3) {
        return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzEiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAzMSA0MCI+CiAgPGRlZnM+CiAgICA8Y2xpcFBhdGggaWQ9ImNsaXAtcGF0aCI+CiAgICAgIDxyZWN0IGlkPSJSZWN0YW5nbGVfNTYwOSIgZGF0YS1uYW1lPSJSZWN0YW5nbGUgNTYwOSIgd2lkdGg9IjMxIiBoZWlnaHQ9IjQwIiBmaWxsPSIjZmZmIi8+CiAgICA8L2NsaXBQYXRoPgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfMTA0NDciIGRhdGEtbmFtZT0iR3JvdXAgMTA0NDciIGNsaXAtcGF0aD0idXJsKCNjbGlwLXBhdGgpIj4KICAgIDxnIGlkPSJHcm91cF8xMDQ0NiIgZGF0YS1uYW1lPSJHcm91cCAxMDQ0NiI+CiAgICAgIDxwYXRoIGlkPSJQYXRoXzQ5MTkiIGRhdGEtbmFtZT0iUGF0aCA0OTE5IiBkPSJNMjYuMyw0LjNBMTUuMjczLDE1LjI3MywwLDAsMCwxNS41LDAsMTUuMzc0LDE1LjM3NCwwLDAsMCwwLDE1LjljMCwzLjIsMS4zLDYuNywzLjgsMTAuNnM2LjUsOC4xLDExLjcsMTIuNmM1LjItNC41LDkuMS04LjcsMTEuNi0xMi42LDIuNi0zLjksMy45LTcuNCwzLjktMTAuNkExNS4yMTQsMTUuMjE0LDAsMCwwLDI2LjMsNC4zWiIgZmlsbD0iIzAwMzNGRiIvPgogICAgICA8cGF0aCBpZD0iUGF0aF80OTIwIiBkYXRhLW5hbWU9IlBhdGggNDkyMCIgZD0iTTE1LjUsMzUuNUE1NS41NjUsNTUuNTY1LDAsMCwxLDYsMjQuN2MtMi4yLTMuNC0zLjMtNi4zLTMuMy04LjhBMTIuNzMyLDEyLjczMiwwLDAsMSw2LjQsNi40YTEyLjI5MywxMi4yOTMsMCwwLDEsOS4xLTMuNywxMi4xMzksMTIuMTM5LDAsMCwxLDkuMSwzLjcsMTIuNzMyLDEyLjczMiwwLDAsMSwzLjcsOS41YzAsMi41LTEuMSw1LjQtMy4yLDguOEE1Ni40NTcsNTYuNDU3LDAsMCwxLDE1LjUsMzUuNVoiIGZpbGw9Im9yYW5nZSIvPgogICAgICA8cGF0aCBpZD0iUGF0aF80OTIxIiBkYXRhLW5hbWU9IlBhdGggNDkyMSIgZD0iTTE1LjUsMzUuNVYyLjdhMTIuMTM5LDEyLjEzOSwwLDAsMSw5LjEsMy43LDEyLjczMiwxMi43MzIsMCwwLDEsMy43LDkuNWMwLDIuNS0xLjEsNS40LTMuMiw4LjhBNTYuNDU3LDU2LjQ1NywwLDAsMSwxNS41LDM1LjVaIiBmaWxsPSIjRkYzMzAwIi8+CiAgICAgIDxwYXRoIGlkPSJQYXRoXzQ5MjIiIGRhdGEtbmFtZT0iUGF0aCA0OTIyIiBkPSJNMTUuNDksMTYuMUgyMS41TDE1LjQ5LDI4LjMzN3YtOC4yTDkuNCwyMC4yLDE1LjQ5LDcuOVoiIGZpbGw9IiNmZmYiLz4KICAgIDwvZz4KICA8L2c+Cjwvc3ZnPgo=';
      } else if (markerData === 4) {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzEiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAzMSA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzI1MzNfNzk3NCkiPgo8cGF0aCBkPSJNMjYuMyA0LjNDMjMuMiAxLjQgMTkuNiAwIDE1LjUgMEMxMS40IDAgNy44IDEuNCA0LjcgNC4zQzEuNiA3LjIgMCAxMS4xIDAgMTUuOUMwIDE5LjEgMS4zIDIyLjYgMy44IDI2LjVDNi4zIDMwLjQgMTAuMyAzNC42IDE1LjUgMzkuMUMyMC43IDM0LjYgMjQuNiAzMC40IDI3LjEgMjYuNUMyOS43IDIyLjYgMzEgMTkuMSAzMSAxNS45QzMxIDExLjEgMjkuNCA3LjIgMjYuMyA0LjNaIiBmaWxsPSIjMDAwMENDIi8+CjxwYXRoIGQ9Ik0xNS41IDM1LjVDMTEuMyAzMS43IDguMiAyOC4xIDYgMjQuN0MzLjggMjEuMyAyLjcgMTguNCAyLjcgMTUuOUMyLjcgMTIgMy45IDguOSA2LjQgNi40QzguOSAzLjkgMTEuOSAyLjcgMTUuNSAyLjdDMTkuMSAyLjcgMjIuMiAzLjkgMjQuNiA2LjRDMjcuMSA4LjkgMjguMyAxMiAyOC4zIDE1LjlDMjguMyAxOC40IDI3LjIgMjEuMyAyNS4xIDI0LjdDMjIuOSAyOC4xIDE5LjcgMzEuNyAxNS41IDM1LjVaIiBmaWxsPSIjMzNDQzAwIi8+CjxwYXRoIGQ9Ik0xNi4xIDE2LjFIMjEuNUwxNS4xIDI4LjRWMjAuMkg5LjRMMTYuMSA3LjlWMTYuMVoiIGZpbGw9IndoaXRlIi8+CjwvZz4KPGRlZnM+CjxjbGlwUGF0aCBpZD0iY2xpcDBfMjUzM183OTc0Ij4KPHJlY3Qgd2lkdGg9IjMxIiBoZWlnaHQ9IjM5LjEiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg==';
      } else if (markerData === 5) {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzEiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAzMSA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzI1MzNfNzk3NCkiPgo8cGF0aCBkPSJNMjYuMyA0LjNDMjMuMiAxLjQgMTkuNiAwIDE1LjUgMEMxMS40IDAgNy44IDEuNCA0LjcgNC4zQzEuNiA3LjIgMCAxMS4xIDAgMTUuOUMwIDE5LjEgMS4zIDIyLjYgMy44IDI2LjVDNi4zIDMwLjQgMTAuMyAzNC42IDE1LjUgMzkuMUMyMC43IDM0LjYgMjQuNiAzMC40IDI3LjEgMjYuNUMyOS43IDIyLjYgMzEgMTkuMSAzMSAxNS45QzMxIDExLjEgMjkuNCA3LjIgMjYuMyA0LjNaIiBmaWxsPSIjMDAwMENDIi8+CjxwYXRoIGQ9Ik0xNS41IDM1LjVDMTEuMyAzMS43IDguMiAyOC4xIDYgMjQuN0MzLjggMjEuMyAyLjcgMTguNCAyLjcgMTUuOUMyLjcgMTIgMy45IDguOSA2LjQgNi40QzguOSAzLjkgMTEuOSAyLjcgMTUuNSAyLjdDMTkuMSAyLjcgMjIuMiAzLjkgMjQuNiA2LjRDMjcuMSA4LjkgMjguMyAxMiAyOC4zIDE1LjlDMjguMyAxOC40IDI3LjIgMjEuMyAyNS4xIDI0LjdDMjIuOSAyOC4xIDE5LjcgMzEuNyAxNS41IDM1LjVaIiBmaWxsPSJvcmFuZ2UiLz4KPHBhdGggZD0iTTE2LjEgMTYuMUgyMS41TDE1LjEgMjguNFYyMC4ySDkuNEwxNi4xIDcuOVYxNi4xWiIgZmlsbD0id2hpdGUiLz4KPC9nPgo8ZGVmcz4KPGNsaXBQYXRoIGlkPSJjbGlwMF8yNTMzXzc5NzQiPgo8cmVjdCB3aWR0aD0iMzEiIGhlaWdodD0iMzkuMSIgZmlsbD0id2hpdGUiLz4KPC9jbGlwUGF0aD4KPC9kZWZzPgo8L3N2Zz4K';
      } else if (markerData === 6) {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzEiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAzMSA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzI1MzNfNzk3NCkiPgo8cGF0aCBkPSJNMjYuMyA0LjNDMjMuMiAxLjQgMTkuNiAwIDE1LjUgMEMxMS40IDAgNy44IDEuNCA0LjcgNC4zQzEuNiA3LjIgMCAxMS4xIDAgMTUuOUMwIDE5LjEgMS4zIDIyLjYgMy44IDI2LjVDNi4zIDMwLjQgMTAuMyAzNC42IDE1LjUgMzkuMUMyMC43IDM0LjYgMjQuNiAzMC40IDI3LjEgMjYuNUMyOS43IDIyLjYgMzEgMTkuMSAzMSAxNS45QzMxIDExLjEgMjkuNCA3LjIgMjYuMyA0LjNaIiBmaWxsPSIjMDAwMENDIi8+CjxwYXRoIGQ9Ik0xNS41IDM1LjVDMTEuMyAzMS43IDguMiAyOC4xIDYgMjQuN0MzLjggMjEuMyAyLjcgMTguNCAyLjcgMTUuOUMyLjcgMTIgMy45IDguOSA2LjQgNi40QzguOSAzLjkgMTEuOSAyLjcgMTUuNSAyLjdDMTkuMSAyLjcgMjIuMiAzLjkgMjQuNiA2LjRDMjcuMSA4LjkgMjguMyAxMiAyOC4zIDE1LjlDMjguMyAxOC40IDI3LjIgMjEuMyAyNS4xIDI0LjdDMjIuOSAyOC4xIDE5LjcgMzEuNyAxNS41IDM1LjVaIiBmaWxsPSIjRkYzMzAwIi8+CjxwYXRoIGQ9Ik0xNi4xIDE2LjFIMjEuNUwxNS4xIDI4LjRWMjAuMkg5LjRMMTYuMSA3LjlWMTYuMVoiIGZpbGw9IndoaXRlIi8+CjwvZz4KPGRlZnM+CjxjbGlwUGF0aCBpZD0iY2xpcDBfMjUzM183OTc0Ij4KPHJlY3Qgd2lkdGg9IjMxIiBoZWlnaHQ9IjM5LjEiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg==';
      }

      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzEiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAzMSA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzI1MzNfNzk3NCkiPgo8cGF0aCBkPSJNMjYuMyA0LjNDMjMuMiAxLjQgMTkuNiAwIDE1LjUgMEMxMS40IDAgNy44IDEuNCA0LjcgNC4zQzEuNiA3LjIgMCAxMS4xIDAgMTUuOUMwIDE5LjEgMS4zIDIyLjYgMy44IDI2LjVDNi4zIDMwLjQgMTAuMyAzNC42IDE1LjUgMzkuMUMyMC43IDM0LjYgMjQuNiAzMC40IDI3LjEgMjYuNUMyOS43IDIyLjYgMzEgMTkuMSAzMSAxNS45QzMxIDExLjEgMjkuNCA3LjIgMjYuMyA0LjNaIiBmaWxsPSIjMDAwMENDIi8+CjxwYXRoIGQ9Ik0xNS41IDM1LjVDMTEuMyAzMS43IDguMiAyOC4xIDYgMjQuN0MzLjggMjEuMyAyLjcgMTguNCAyLjcgMTUuOUMyLjcgMTIgMy45IDguOSA2LjQgNi40QzguOSAzLjkgMTEuOSAyLjcgMTUuNSAyLjdDMTkuMSAyLjcgMjIuMiAzLjkgMjQuNiA2LjRDMjcuMSA4LjkgMjguMyAxMiAyOC4zIDE1LjlDMjguMyAxOC40IDI3LjIgMjEuMyAyNS4xIDI0LjdDMjIuOSAyOC4xIDE5LjcgMzEuNyAxNS41IDM1LjVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0xNi4xIDE2LjFIMjEuNUwxNS4xIDI4LjRWMjAuMkg5LjRMMTYuMSA3LjlWMTYuMVoiIGZpbGw9IndoaXRlIi8+CjwvZz4KPGRlZnM+CjxjbGlwUGF0aCBpZD0iY2xpcDBfMjUzM183OTc0Ij4KPHJlY3Qgd2lkdGg9IjMxIiBoZWlnaHQ9IjM5LjEiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg==';
    },
    refreshMap() {
      this.map.panBy(0, 0); // sometimes enough
    },
    setMarkers: async function () {
      this.newMarkers.forEach((location) => {
        const image = document.createElement('img');
        image.src = this.assignPin(location.final_status, location.availability, location.emergency_stop_status);

        const key = location.calculated_latitude + '-' + location.calculated_longitude;
        const marker = new this.advancedMarkerElement({
          position: {
            lat: parseFloat(location.calculated_latitude),
            lng: parseFloat(location.calculated_longitude),
          },
          map: mapInstance,
          content: image,
          title: location.names,
        });

        this.markers[key] = { marker, location };

        marker.addListener('click', async () => {
          const markerData = this.markers[key].location;
          if (markerData.count === 1) {
            const data = location.evse_id[0].split(';');
            if (+data[2] === 1) {
              window.location.href = '/admin/cp/dashboard/' + data[3];
            } else {
              this.infoWindow.setContent(this.localMarkerGetContent(key));
              this.infoWindow.open(null, marker);
            }
          } else {
            this.infoWindow.setContent(this.localMarkerGetContent(key));
            this.infoWindow.open(null, marker);
          }
        });
      });
    },
    localMarkerGetContent: function (locationId) {
      const location = this.markers[locationId].location;
      let contentString =
        '<div id="content">' + '<h3 style="margin-bottom: 20px;">Charge Point Data</h3>' + '<div id="bodyContent">';

      for (const [index, evse_id] of location.evse_id.entries()) {
        const data = evse_id.split(';');
        contentString += `<p style="margin-bottom: 0;"><b>Status:</b> ${data[1]}</p>`;

        if (+data[2] === 1) {
          contentString += `<a href="/admin/cp/dashboard/${data[3]}" style="margin-top: 10px;">
            <p style="margin-bottom: 0;"><b>Evse ID:</b> ${data[0]}<img style="max-width: 20px; margin-left: 5px; cursor: pointer;"
                        onclick="navigator.clipboard.writeText('${data[0]}');"
                        src="/assets/images/copy.png" /></p>
</a>`;
        } else {
          contentString += `<p style="margin-bottom: 0;"><b>Evse ID:</b> ${data[0]}<img style="max-width: 20px; margin-left: 5px; cursor: pointer;"
                        onclick="navigator.cipboard.writeText('${data[0]}');"
                        src="/assets/images/copy.png" /></p>`;
        }

        if (index < location.evse_id.length) {
          contentString += '<hr />';
        }
      }

      contentString += '</div>' + '</div>';
      return contentString;
    },
    prepareHubjectMarkers: function (hubjectMarkers) {
      const bounds = this.map.getBounds();

      // remove exiting markers that are out of bounds
      if (bounds) {
        for (const [key, value] of Object.entries(this.hubjectMarkers)) {
          const position = value.marker.position;
          if (position && bounds.contains(position)) {
            continue;
          }

          value.marker.setMap(null);
          delete this.hubjectMarkers[key];
        }
      }

      for (let j = 0; j < hubjectMarkers.length; j++) {
        const markerData = hubjectMarkers[j];
        const key = markerData.latitude + '_' + markerData.longitude;

        // remove new markers that are out of bounds or already exist
        const position = {
          lat: parseFloat(markerData.latitude),
          lng: parseFloat(markerData.longitude),
        };

        if (!bounds || !bounds.contains(position)) {
          continue;
        }

        // if marker already exists, skip
        if (this.hubjectMarkers[key]) {
          continue;
        }

        const pinImage = document.createElement('img');
        pinImage.src = this.assignHubjectPin(markerData.status);

        let marker = new this.advancedMarkerElement({
          position: position,
          title: '' + key,
          content: pinImage,
        });

        marker.addListener('click', async () => {
          const data = await this.getHubjectDescription(key);
          const description = data.description;
          if (!description) {
            return;
          }

          this.infoWindow.setContent(this.renderDescription(description.description, data['prices'], data['powers']));
          this.infoWindow.open(null, marker);
        });

        this.hubjectMarkers[key] = { marker, status: markerData.status };
      }

      // if too many render clusters
      if (Object.keys(this.hubjectMarkers).length >= 200) {
        this.renderClusters(13);
        return;
      }

      this.cleanClusters();

      // set all markers on map
      for (const key in this.hubjectMarkers) {
        const hubjectMarker = this.hubjectMarkers[key];
        if (hubjectMarker && hubjectMarker.marker) {
          hubjectMarker.marker.setMap(this.map);
        }
      }

      this.refreshMap();
    },
    renderDescription: function (description, prices, powers) {
      let contentString =
        '<div id="content">' + '<h3 style="margin-bottom: 20px;">Charge Point Data</h3>' + '<div id="bodyContent">';

      let i = 0;
      const chargePoints = description.split(';');
      for (const chargePoint of chargePoints) {
        const chargePointData = chargePoint.split(',');
        contentString +=
          `<p style="margin-bottom: 0;"><b>Status:</b> ${chargePointData[1] ?? 'Status Not Available'}</p>` +
          `<p style="margin-bottom: 0;"><b>Evse ID:</b> ${chargePointData[0]}<img style="max-width: 20px; margin-left: 5px; cursor: pointer;"
                      onclick="navigator.clipboard.writeText('${chargePointData[0]}');"
                      src="/assets/images/copy.png" /></p>`;
        if (powers && powers[chargePointData[0]]) {
          contentString += `<p style="margin-bottom: 0;"><b>Power:</b> ${powers[chargePointData[0]]} kW</p>`;
        }

        //// calculation for dynamic pricing
        // if (prices && prices[chargePointData[0]] && prices[chargePointData[0]]['best_price']) {
        //   const bestPrice = prices[chargePointData[0]]['best_price'];
        //   contentString +=
        //     '<p style="margin-bottom: 2px;">---</p>' +
        //     `<p style="margin-bottom: 2px;"><b>Price:</b> ${bestPrice.price} (${bestPrice.reference_unit})</p>`;
        //
        //   if (bestPrice.additional && bestPrice.additional.length) {
        //     for (const additionalElement of bestPrice.additional) {
        //       contentString += `<p style="margin-bottom: 2px;">
        //         <b>${this.capitalizeString(additionalElement.reference_type)}:</b>
        //         ${additionalElement.price_per_unit} (${additionalElement.reference_unit})</p>`;
        //     }
        //   }
        //
        //   contentString += `<p style="margin-bottom: 2px;"><b>Currency:</b> ${bestPrice.currency}</p>`;
        // }

        if (prices && prices[chargePointData[0]] && prices[chargePointData[0]]['price']) {
          const price = prices[chargePointData[0]];
          contentString +=
            '<p style="margin-bottom: 2px;">---</p>' +
            `<p style="margin-bottom: 2px;"><b>Price:</b> ${price.price} (${price.pricing_type})</p>`;

          contentString += `<p style="margin-bottom: 2px;"><b>Currency:</b>EUR</p>`;
        }

        i++;
        if (i < chargePoints.length) {
          contentString += '<hr />';
        }
      }

      contentString += '</div>' + '</div>';
      return contentString;
    },
    initMap: async function () {
      const center = this.center.split('_');
      const home = { lat: +center[0], lng: +center[1] };
      // const home = { lat: +52.3676, lng: 4.9041 }; // Amsterdam center

      const mapOptions = {
        center: home,
        zoom: this.zoom,
        mapId: this.mapId,
      };

      const { Map, InfoWindow } = await google.maps.importLibrary('maps');

      mapInstance = new Map(document.getElementById('map'), mapOptions);

      this.map = mapInstance;

      this.infoWindow = new InfoWindow({
        content: '',
      });

      const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
      this.advancedMarkerElement = AdvancedMarkerElement;
    },
    updateHubjectMarkers: function (hubjectMarkers) {
      for (const hubjectMarker of hubjectMarkers) {
        const key = hubjectMarker.latitude + '_' + hubjectMarker.longitude;
        if (this.hubjectMarkers[key] && this.hubjectMarkers[key]['status'] !== hubjectMarker.status) {
          this.hubjectMarkers[key]['marker'].content.src = this.assignHubjectPin(hubjectMarker.status);
          this.hubjectMarkers[key]['status'] = hubjectMarker.status;
        }
      }
    },
    renderClusters: async function (zoom) {
      if (!this.showClusters) {
        return;
      }

      // console.log('Clustered markers from zoom level', zoom);
      this.cleanHubjectMarkers();
      if (this.clusterOnScreen && this.currentZoomLevel === zoom) {
        return;
      }

      this.currentZoomLevel = zoom;
      let clusters = null;

      const localStorageExpiry = localStorage.getItem(`hubject_clusters_${zoom}_expiry`);
      const now = Date.now();

      if (this.clusters[zoom]) {
        // console.log('Clustered markers from cache for zoom level', zoom);
        clusters = this.clusters[zoom];
      } else if (localStorageExpiry && now < localStorageExpiry) {
        // console.log('Clustered markers from localStorage for zoom level', zoom);
        const localStorageClusters = localStorage.getItem(`hubject_clusters_${zoom}`);
        if (localStorageClusters) {
          const decompressed = decompressFromUTF16(localStorageClusters);
          clusters = JSON.parse(decompressed);
          this.clusters[zoom] = clusters;
        }
      }

      if (!this.clusters[zoom]) {
        const success = await this.getClustersFromServer(zoom);
        if (success === false) {
          return;
        }

        clusters = this.clusters[zoom];
      }

      this.cleanClusters();
      const canvasCreatorClass = window.canvasCreator(google);
      this.currentOverlay = new canvasCreatorClass(this.map, clusters);
      this.currentOverlay.setMap(this.map);
      this.clusterOnScreen = true;
    },
    cleanClusters: function () {
      if (this.currentOverlay) {
        this.currentOverlay.setMap(null);
        this.currentOverlay = null;
      }

      this.clusterOnScreen = false;
    },
    getClustersFromServer: async function (zoom) {
      // console.log('Clustered markers from database for zoom level', zoom);
      try {
        const response = await axios.get('admin/hubject-emp/map-clusters', {
          params: {
            zoom_level: zoom,
          },
        });

        const clusters = response.data.clusters;
        this.clusters[zoom] = clusters;
        const jsonString = JSON.stringify(clusters);
        const compressedClusters = compressToUTF16(jsonString);
        // console.log('Clustered markers from database for zoom level', zoom, 'count', compressedClusters.length);
        // save in  localStorage
        localStorage.setItem(`hubject_clusters_${zoom}`, compressedClusters);
        // save in localStorage expiry as current time + 24 hours
        localStorage.setItem(`hubject_clusters_${zoom}_expiry`, Date.now() + 24 * 60 * 60 * 1000);
      } catch (error) {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.log(error.response);
        } else {
          console.log('Error', error);
        }

        return false;
      }
    },
    mysqlRound: function (num, decimals = 5) {
      const factor = Math.pow(10, decimals);
      const rounded = Math.round(num * factor) / factor;
      return rounded.toFixed(decimals);
    },
  },
  mounted: async function () {
    await this.initMap();
    let loaded = false;
    this.map.addListener('zoom_changed', () => {
      // this.processZoom();
    });

    this.map.addListener('idle', async () => {
      const zoom = Math.round(this.map.getZoom());
      if (!loaded) {
        loaded = true;
        // this.getGroupedData();
        this.getData();
        window.setInterval(this.getData, 10000);
        // window.setInterval(() => {
        //   this.getHubjectData(true, Object.keys(this.geoHubjectMarkers));
        // }, 60000);
        await this.renderClusters(zoom);
        // start in background loading clusters for zoom levels 13
        // but only if they expired in localStorage
        const localStorageExpiry = localStorage.getItem('hubject_clusters_13_expiry');
        const now = Date.now();
        if (localStorageExpiry && now < localStorageExpiry) {
          return;
        }

        this.initial13Load = this.getClustersFromServer(13);
        await this.initial13Load;
        // console.log('Initial clusters for zoom level 13 loaded in background');
        this.initial13Load = null;

        return;
      }

      if (zoom >= 15) {
        // console.log('Detailed markers for zoom level', zoom);
        await this.getHubjectData();
        setTimeout(() => this.refreshMap(), 1);
        // console.log('Set detailed markers for zoom level', zoom);
      } else if (zoom <= 4) {
        this.cleanClusters();
        this.cleanHubjectMarkers();
      } else if (zoom <= 13) {
        // if we are in zoom 13, and we have not loaded clusters for 13, wait for it
        if (zoom === 13 && this.initial13Load) {
          // console.log('Waiting for initial clusters for zoom level 13 to load');
          await this.initial13Load;
          this.initial13Load = null;
        }

        await this.renderClusters(zoom);
      } else {
        await this.renderClusters(13);
      }
    });

    // manually trigger idle to load data
    google.maps.event.trigger(this.map, 'idle');
  },
};
</script>
